/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {Component} from 'react';
import Spinner from 'react-spinkit';
import ReactTooltip from 'react-tooltip';
import {createFragmentContainer} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import {History} from 'history';

import ErrorView from '../../components/ErrorView';
import AccountSelector from '../../AccountSelector';
import EntityCombobox from '../../components/EntityCombobox';
import DashboardSummary from './DashboardSummary';
import DashboardSearch from './DashboardSearch';
import {DashboardSpokePerformance} from './DashboardSpokePerformance';
import {DashboardProductsReport} from './DashboardProductsReport';
import DashboardSearchButtonSet from './DashboardSearchButtonSet';
import DashboardNegativeSummary from './DashboardNegativeSummary';
import {DashboardDataViewer_viewer} from './__generated__/DashboardDataViewer_viewer.graphql';

import DataViewerStyles from './DashboardDataViewer.module.css';
import ErrorDisplay from '../../../components/errorDisplay';
import {DashboardNewProductsModal} from './DashboardNewProductsModal';
import {DashboardDropDown} from './DashboardDropDown';

const Selectors = ({
  isLoading,
  viewer,
  subEntityReference,
  handleEntityReferenceChanged,
  handleSubEntityReferenceChanged,
}: {
  isLoading: boolean;
  subEntityReference: string | null;
  viewer: DashboardDataViewer_viewer;
  handleEntityReferenceChanged: (entityReference: string | null) => void;
  handleSubEntityReferenceChanged: (entityReference: string | null) => void;
}) => (
  <div className="combo-boxes">
    <div className="combo-stabilizer">
      <AccountSelector
        viewer={viewer}
        loading={isLoading}
        onSelectedEntityChanged={(entityReference) => {
          handleEntityReferenceChanged(entityReference);
        }}
      />
      <EntityCombobox
        viewer={viewer}
        loading={isLoading}
        // Only allow rollups for hub
        includeRollup={viewer.dashboard ? !!viewer.dashboard.hub : false}
        handleChange={(newEntityReference) => {
          handleSubEntityReferenceChanged(newEntityReference);
        }}
        handleReset={() => {
          handleSubEntityReferenceChanged(null);
        }}
        value={subEntityReference}
      />
      {(!!sessionStorage.getItem('spoke') ||
        !!sessionStorage.getItem('hub')) && (
        <button
          className={DataViewerStyles.resetButton}
          onClick={() => {
            sessionStorage.removeItem('spoke');
            sessionStorage.removeItem('hub');
            handleSubEntityReferenceChanged(null);
            handleEntityReferenceChanged(null);
          }}
        >
          Reset Dashboard (to my Hub)
        </button>
      )}
    </div>
  </div>
);

interface Props {
  error?: Error;
  openDataModal: (open: string) => any;
  isModalOpen: boolean;
  viewer: DashboardDataViewer_viewer;
  isLoading: boolean;
  entityReference: string;
  handleNegativeSummaryClick: () => void;
  handleProfitSummaryClick: () => void;
  subEntityReference: string | null;
  handleEntityReferenceChanged: (entityReference: string | null) => void;
  handleSubEntityReferenceChanged: (subEntityReference: string | null) => void;
  history: History;
}

interface State {
  mobileSearchModalVisible: boolean;
}

class DashboardDataViewer extends Component<Props, State> {
  private jumpRefs = {
    balance: React.createRef<HTMLDivElement>(),
    sales: React.createRef<HTMLDivElement>(),
    airtime: React.createRef<HTMLDivElement>(),
    data: React.createRef<HTMLDivElement>(),
    electricity: React.createRef<HTMLDivElement>(),
    hollywood: React.createRef<HTMLDivElement>(),
    unipin: React.createRef<HTMLDivElement>(),
    dstv: React.createRef<HTMLDivElement>(),
    lotto: React.createRef<HTMLDivElement>(),
    m2m: React.createRef<HTMLDivElement>(),
    other: React.createRef<HTMLDivElement>(),
    ricatotal: React.createRef<HTMLDivElement>(),
    ricasuccess: React.createRef<HTMLDivElement>(),
    ricafail: React.createRef<HTMLDivElement>(),
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      mobileSearchModalVisible: false,
    };
  }

  public openSearchModal = () => {
    this.setState({mobileSearchModalVisible: true});
  };

  public scrollToComponent = (value: any) => {
    const target = this.jumpRefs[value];

    if (target && target.current) {
      window.scrollTo({
        left: 0,
        top: target.current.offsetTop - 100,
      });
    } else {
      // tslint:disable-next-line no-console
      console.log(`Could not find ref: ${value} to scroll to`, this.jumpRefs);
    }
  };

  public render() {
    const {
      viewer,
      openDataModal,
      isLoading,
      isModalOpen,
      handleSubEntityReferenceChanged,
      handleEntityReferenceChanged,
      history,
    } = this.props;

    const detailedSalesReportDateRange = viewer.report
      ? viewer.report.detailedSalesReportDateRange
      : undefined;

    document.body.style.overflow =
      this.state.mobileSearchModalVisible || isModalOpen ? 'hidden' : 'scroll';

    // const isSpokeAccount = false;

    if (!viewer.dashboard) {
      return (
        <div>
          <div>
            <Selectors
              isLoading={isLoading}
              subEntityReference={this.props.subEntityReference}
              viewer={viewer}
              handleEntityReferenceChanged={handleEntityReferenceChanged}
              handleSubEntityReferenceChanged={handleSubEntityReferenceChanged}
            />
          </div>
          <div className={DataViewerStyles['dashboard-error-display']}>
            <ErrorDisplay errorText="This entity has no data associated to it." />
          </div>
        </div>
      );
    }

    if (isModalOpen) {
      // Do not render dashboard when modal is open
      // to help performance on older devices
      return null;
    }

    const {dashboard} = viewer;

    // TODO fix any
    let account: any = null;

    if (
      this.props.entityReference === this.props.subEntityReference &&
      dashboard.hub
    ) {
      account = dashboard.hub;
    } else if (this.props.subEntityReference === 'rollup') {
      account = dashboard.rollup;
    } else {
      account = dashboard.spokes.find((spoke) => {
        return spoke.account === this.props.subEntityReference;
      });
    }
    function getCategory(category: string) {
      const c = account.categories.find((cat) => cat.category === category);

      if (!c) {
        throw new Error(`Could not find Category: ${category}`);
      }

      return c;
    }

    // Only hubs can see negative trading summary
    const showNegativeSummary = !!viewer.dashboard.hub;
    // If we cannot find an account and the refetch is has completed display an error
    return (
      <div className={DataViewerStyles.container}>
        <DashboardNewProductsModal />
        {this.state.mobileSearchModalVisible ? (
          <DashboardSearch
            account={account}
            onClose={() => {
              this.setState({mobileSearchModalVisible: false});
            }}
            scrollToComponent={this.scrollToComponent}
          />
        ) : (
          !isModalOpen && (
            <DashboardSearchButtonSet openSearchModal={this.openSearchModal} />
          )
        )}
        <Selectors
          isLoading={isLoading}
          subEntityReference={this.props.subEntityReference}
          viewer={viewer}
          handleEntityReferenceChanged={handleEntityReferenceChanged}
          handleSubEntityReferenceChanged={handleSubEntityReferenceChanged}
        />
        {this.props.error && <ErrorView error={this.props.error} />}
        {account && (
          <>
            {showNegativeSummary && (
              <DashboardSpokePerformance
                data={detailedSalesReportDateRange}
                handleItemClick={this.props.handleSubEntityReferenceChanged}
              />
            )}
            {showNegativeSummary && (
              <DashboardProductsReport
                // TODO: fix this type error
                // @ts-expect-error type error
                salesData={viewer.report!.detailedSalesReport!}
                history={history}
                // handleItemClick={this.props.handleSubEntityReferenceChanged}
              />
            )}
            <DashboardSummary
              totalSalesRef={this.jumpRefs.sales}
              subEntityReference={this.props.subEntityReference}
              commissionSummary={
                this.props.viewer.report!.commissionSummary as any
              }
              handleClick={this.props.handleProfitSummaryClick}
              balanceRef={this.jumpRefs.balance}
              accountSummary={account}
              openDataModal={openDataModal}
              totalSales={getCategory('TOTAL_SALES')}
            />
          </>
        )}
        {!account && !isLoading && (
          <ErrorView error={new Error('Account not found')} />
        )}
        {isLoading && (
          <Spinner
            name="circle"
            color="darkblue"
            fadeIn="none"
            style={{margin: '0 auto', zIndex: 20, width: 30, height: 30}}
          />
        )}

        {account && (
          <div className={DataViewerStyles.dataRow}>
            {viewer.report?.detailedSalesReport?.map((value) => {
              if (this.props.subEntityReference === 'rollup') {
                if (value?.categories?.airtime?.totalRollup?.key === 'rollUp') {
                  const returnArray: any = [];
                  value?.categories?.airtime &&
                    !value.categories.airtime.totalRollup.isNull &&
                    returnArray.push(
                      <DashboardDropDown
                        openDataModal={openDataModal}
                        content={value?.categories?.airtime}
                        key={'airtimeDropDown'}
                      />
                    );
                  value?.categories?.data &&
                    !value.categories.data.totalRollup.isNull &&
                    returnArray.push(
                      <DashboardDropDown
                        openDataModal={openDataModal}
                        content={value?.categories?.data}
                        key={'dataDropDown'}
                      />
                    );
                  value?.categories?.entertainment &&
                    !value.categories.entertainment.totalRollup.isNull &&
                    returnArray.push(
                      <DashboardDropDown
                        openDataModal={openDataModal}
                        content={value?.categories?.entertainment}
                        key={'entertainmentDropDown'}
                      />
                    );
                  value?.categories?.billPayments &&
                    !value.categories.billPayments.totalRollup.isNull &&
                    returnArray.push(
                      <DashboardDropDown
                        openDataModal={openDataModal}
                        key={'billPaymentsDropDown'}
                        content={value?.categories?.billPayments}
                      />
                    );
                  value?.categories?.ticketing &&
                    !value.categories.ticketing.totalRollup.isNull &&
                    returnArray.push(
                      <DashboardDropDown
                        openDataModal={openDataModal}
                        content={value?.categories?.ticketing}
                        key={'ticketingDropDown'}
                      />
                    );
                  value?.categories?.electricity &&
                    !value.categories.electricity.totalRollup.isNull &&
                    returnArray.push(
                      <DashboardDropDown
                        openDataModal={openDataModal}
                        content={value?.categories?.electricity}
                        key={'electricityDropDown'}
                      />
                    );
                  value?.categories?.other &&
                    !value.categories.other.totalRollup.isNull &&
                    returnArray.push(
                      <DashboardDropDown
                        openDataModal={openDataModal}
                        content={value?.categories?.other}
                        key={'otherDropDown'}
                      />
                    );
                  return returnArray;
                }
              } else if (value?.entity === account.account) {
                const returnArray: any = [];
                value?.categories?.airtime &&
                  returnArray.push(
                    <DashboardDropDown
                      openDataModal={openDataModal}
                      content={value?.categories?.airtime}
                      key={'airtimeDropDown'}
                    />
                  );
                value?.categories?.data &&
                  returnArray.push(
                    <DashboardDropDown
                      openDataModal={openDataModal}
                      content={value?.categories?.data}
                      key={'dataDropDown'}
                    />
                  );
                value?.categories?.entertainment &&
                  returnArray.push(
                    <DashboardDropDown
                      openDataModal={openDataModal}
                      content={value?.categories?.entertainment}
                      key={'entertainmentDropDown'}
                    />
                  );
                value?.categories?.billPayments &&
                  returnArray.push(
                    <DashboardDropDown
                      openDataModal={openDataModal}
                      key={'billPaymentsDropDown'}
                      content={value?.categories?.billPayments}
                    />
                  );
                value?.categories?.ticketing &&
                  returnArray.push(
                    <DashboardDropDown
                      openDataModal={openDataModal}
                      content={value?.categories?.ticketing}
                      key={'ticketingDropDown'}
                    />
                  );
                value?.categories?.electricity &&
                  returnArray.push(
                    <DashboardDropDown
                      openDataModal={openDataModal}
                      content={value?.categories?.electricity}
                      key={'electricityDropDown'}
                    />
                  );
                value?.categories?.other &&
                  returnArray.push(
                    <DashboardDropDown
                      openDataModal={openDataModal}
                      content={value?.categories?.other}
                      key={'otherDropDown'}
                    />
                  );
                return returnArray;
              }
            })}

            {showNegativeSummary && (
              <DashboardNegativeSummary
                title="Negative Trading Summary"
                handleClick={this.props.handleNegativeSummaryClick}
                viewer={viewer}
              />
            )}
          </div>
        )}
        <ReactTooltip />
      </div>
    );
  }
}

export default createFragmentContainer(DashboardDataViewer, {
  viewer: graphql`
    fragment DashboardDataViewer_viewer on Viewer {
      ...AccountSelector_viewer
      user {
        roles {
          name
        }
      }
      report {
        commissionSummary(entityReference: $entityReference) {
          ...DashboardSummary_commissionSummary
        }
        detailedSalesReportDateRange(
          entityReference: $entityReference
          startDate: $startDate
          endDate: $endDate
        ) {
          HUBAcc
          HUB
          SpokeAcc
          Spoke
          TotalSales
          Airtime_Sales
          Data_Sales
          Electricity_Sales
          Entertainment_Sales
          Ticketing_Sales
          Billpayments_Sales
          Other_Sales
        }
        detailedSalesReport(entityReference: $entityReference) {
          ...DashboardDropDown_report
          ...DashboardProductsReport_report
          entity
          categories {
            airtime {
              categories {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              totalRollup {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              transactions {
                transactionCount
                transactionAmount
              }
            }
            data {
              categories {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              totalRollup {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              transactions {
                transactionCount
                transactionAmount
              }
            }
            entertainment {
              categories {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              totalRollup {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              transactions {
                transactionCount
                transactionAmount
              }
            }
            ticketing {
              categories {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              totalRollup {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              transactions {
                transactionCount
                transactionAmount
              }
            }
            billPayments {
              categories {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              totalRollup {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              transactions {
                transactionCount
                transactionAmount
              }
            }
            electricity {
              categories {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              totalRollup {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              transactions {
                transactionCount
                transactionAmount
              }
            }
            other {
              categories {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              totalRollup {
                isNull
                name
                key
                yesterdaysSalesToDate
                currentMonthToDate
                lastMonthMonthToDate
                lastMonth
              }
              transactions {
                transactionCount
                transactionAmount
              }
            }
          }
        }
      }
      ...EntityCombobox_viewer
      ...DashboardNegativeSummary_viewer
      dashboard(entityReference: $entityReference) {
        hub {
          account
          ...DashboardSearch_account
          ...DashboardSummary_accountSummary
          categories {
            category
            ...DashboardDataBlock_category
            ...DashboardSummary_totalSales
          }
        }
        rollup {
          account
          ...DashboardSearch_account
          ...DashboardSummary_accountSummary
          categories {
            category
            ...DashboardDataBlock_category
            ...DashboardSummary_totalSales
          }
        }
        spokes {
          account
          ...DashboardSearch_account
          ...DashboardSummary_accountSummary
          categories {
            category
            ...DashboardDataBlock_category
            ...DashboardSummary_totalSales
          }
        }
      }
    }
  `,
});
